import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { useCitation } from '../Providers/CitationProvider';
import { useGetACampaignDetails } from './useGetACampaignDetails';

interface UseCampaignDataOptions {
  /**
   * Custom error message for failed API requests
   * @default 'Failed to get campaign data. Please try again.'
   */
  errorMessage?: string;
  /**
   * Custom error message for incomplete lookup status
   * @default 'Failed to get campaign data. Please try again.'
   */
  incompleteLookupMessage?: string;
  /**
   * Redirect path on error
   * @default '/local-seo/citations'
   */
  redirectPath?: string;
  /**
   * Whether to require campaignId (redirect if missing)
   * @default true
   */
  requireCampaignId?: boolean;
}

interface UseCampaignDataReturn {
  campaignId: string | undefined;
  isLoading: boolean;
  shouldShowLoading: boolean;
  hasError: boolean;
}

/**
 * Custom hook to handle campaign data restoration and fetching
 * - Restores user selections from localStorage
 * - Fetches campaign details if missing
 * - Handles loading and error states
 */
export const useCampaignData = (
  options: UseCampaignDataOptions = {}
): UseCampaignDataReturn => {
  const {
    errorMessage = 'Failed to get campaign data. Please try again.',
    incompleteLookupMessage = 'Failed to get campaign data. Please try again.',
    redirectPath = '/local-seo/citations',
    requireCampaignId = true
  } = options;

  const { state: locationState } = useLocation();
  const navigate = useNavigate();
  const { setError } = useCitation();
  const { campaignId } = (locationState as { campaignId: string }) || {};

  const {
    state,
    setCampaignDetails,
    restoreStateFromStorage
  } = useCitation();

  // Restore user selections from localStorage on mount
  useEffect(() => {
    if (campaignId) {
      restoreStateFromStorage();
    }
  }, [campaignId, restoreStateFromStorage]);

  // Fetch campaign details if missing (e.g., after refresh)
  const shouldFetch = !state.campaignDetails && !!campaignId;
  const { data: campaignData, isLoading } = useGetACampaignDetails({
    campaignId: shouldFetch ? campaignId : undefined,
    refetch: false
  });

  // Handle campaign data response
  useEffect(() => {
    if (!campaignData || state.campaignDetails) {
      return;
    }

    // Handle error case
    if (campaignData.status !== 200) {
      setError({
          status: 'error',
          message: errorMessage
        });
      navigate(redirectPath);
      return;
    }

    const lookupStatus = campaignData.data?.response?.lookup_status;

    // Handle completion case
    if (lookupStatus === 'complete') {
      setCampaignDetails(campaignData.data?.response);
    } else {
      setError({
          status: 'error',
          message: incompleteLookupMessage
        });
      navigate(redirectPath);
    }
  }, [
    campaignData,
    state.campaignDetails,
    navigate,
    setCampaignDetails,
    errorMessage,
    incompleteLookupMessage,
    redirectPath
  ]);

  // Redirect if campaignId is required but missing
  useEffect(() => {
    if (requireCampaignId && !campaignId) {
      navigate(redirectPath);
    }
  }, [campaignId, requireCampaignId, navigate, redirectPath]);

  const shouldShowLoading = isLoading && !state.campaignDetails;
  const hasError = campaignData?.status !== 200 || 
    (campaignData?.data?.response?.lookup_status !== 'complete' && campaignData?.data?.response?.lookup_status !== undefined);

  return {
    campaignId,
    isLoading,
    shouldShowLoading,
    hasError
  };
};

